import React from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Heart, Flame, Brain, Network } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';

interface DomainSelectorProps {
  onSelect: (domainKey: string) => void;
  onSkip: () => void;
}

interface EffectiveDomain {
  id: string;
  domain_key: string;
  name_en: string;
  name_zh: string;
  description_en: string | null;
  description_zh: string | null;
  icon_name: string | null;
  sort_order: number;
}

const DomainSelector: React.FC<DomainSelectorProps> = ({ onSelect, onSkip }) => {
  const { t, i18n } = useTranslation('service-selection');
  const isZh = i18n.language === 'zh';

  const { data: domains, isLoading, error } = useQuery({
    queryKey: ['effective-domains'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('effective_domains')
        .select('*')
        .eq('is_active', true)
        .order('sort_order', { ascending: true });

      if (error) throw error;
      return data as EffectiveDomain[];
    }
  });

  const getIcon = (iconName: string | null) => {
    const iconProps = { className: "h-8 w-8 text-primary" };
    switch (iconName) {
      case 'Heart': return <Heart {...iconProps} />;
      case 'Flame': return <Flame {...iconProps} />;
      case 'Brain': return <Brain {...iconProps} />;
      case 'Network': return <Network {...iconProps} />;
      default: return <Heart {...iconProps} />;
    }
  };

  if (isLoading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {[...Array(4)].map((_, i) => (
          <Skeleton key={i} className="h-48 w-full rounded-xl" />
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-destructive mb-4">Failed to load domains</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <p className="text-center text-muted-foreground mb-6">
        {t('selectDomain')}
      </p>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {domains?.map((domain) => (
          <button
            key={domain.id}
            onClick={() => onSelect(domain.domain_key)}
            className="p-6 rounded-xl border-2 border-border bg-card hover:border-primary hover:shadow-lg transition-all duration-300 hover:scale-105 text-center"
          >
            <div className="flex flex-col items-center gap-4">
              <div className="p-4 rounded-full bg-primary/10">
                {getIcon(domain.icon_name)}
              </div>
              <div>
                <h3 className="text-2xl font-bold text-foreground mb-1">
                  {isZh ? domain.name_zh : domain.name_en}
                </h3>
                <p className="text-sm text-muted-foreground">
                  {isZh ? domain.description_zh : domain.description_en}
                </p>
              </div>
            </div>
          </button>
        ))}
      </div>

      <div className="flex justify-center pt-4">
        <Button variant="outline" onClick={onSkip} size="lg">
          {t('skipButton')}
        </Button>
      </div>
    </div>
  );
};

export default DomainSelector;
